import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { MaintenanceReport, MAINTENANCE_PROBLEMS } from '@/models/maintenance';
import { getCompletedMaintenanceReports, deleteCompletedMaintenanceReport } from '@/services/maintenanceService';
import { useData } from '@/contexts/DataContext';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { Input } from '@/components/ui/input';
import { Loader2, Search, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface CompletedMaintenanceListProps {
  screenId?: string;
}

const CompletedMaintenanceList: React.FC<CompletedMaintenanceListProps> = ({ screenId }) => {
  const [completedReports, setCompletedReports] = useState<MaintenanceReport[]>([]);
  const [allReports, setAllReports] = useState<MaintenanceReport[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [reportToDelete, setReportToDelete] = useState<MaintenanceReport | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const { screens } = useData();

  // Cargar los reportes completados
  const loadCompletedReports = async () => {
    setLoading(true);
    try {
      let reports = await getCompletedMaintenanceReports();
      
      // Filtrar por pantalla si se proporciona un ID y no es la vista de historial completo
      if (screenId && !window.location.search.includes('tab=completed')) {
        reports = reports.filter(report => report.screenId === screenId);
      }
      
      // Importante: Mostrar todos los reportes en el historial, incluso los que tienen is_dismissed=true
      // No filtramos por isDismissed aquí para mostrar el historial completo
      
      setAllReports(reports);
      setCompletedReports(reports);
    } catch (error) {
      console.error("Error al cargar reportes completados:", error);
      toast.error("Error al cargar el historial de mantenimientos");
    } finally {
      setLoading(false);
    }
  };

  // Cargar reportes al montar el componente y cuando cambian las pantallas o el screenId
  useEffect(() => {
    loadCompletedReports();
  }, [screens, screenId]);

  // Filtrar reportes cuando cambia el término de búsqueda
  useEffect(() => {
    if (!searchTerm.trim()) {
      setCompletedReports(allReports);
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    const filtered = allReports.filter(report => {
      // Buscar en nombre de pantalla
      const screenName = report.screen?.name?.toLowerCase() || '';
      if (screenName.includes(searchLower)) return true;

      // Buscar en ubicación de pantalla
      const screenLocation = report.screen?.location?.toLowerCase() || '';
      if (screenLocation.includes(searchLower)) return true;

      // Buscar en problemas
      const problemsMatch = report.problems.some(problemType => {
        const problem = MAINTENANCE_PROBLEMS.find(p => p.type === problemType);
        return problem?.label.toLowerCase().includes(searchLower);
      });
      if (problemsMatch) return true;

      // Buscar en fecha de completado
      if (report.completedAt) {
        const formattedDate = format(parseISO(report.completedAt), 'dd/MM/yyyy HH:mm');
        if (formattedDate.includes(searchLower)) return true;
      }

      return false;
    });

    setCompletedReports(filtered);
  }, [searchTerm, allReports]);

  // Obtener los nombres de los problemas seleccionados
  const getSelectedProblems = (problems: string[]) => {
    return problems.map(problemType => {
      const problem = MAINTENANCE_PROBLEMS.find(p => p.type === problemType);
      return problem ? `${problem.icon} ${problem.label}` : problemType;
    }).join(', ');
  };

  // Título que muestra si se está filtrando por una pantalla específica
  const getTitle = () => {
    if (screenId && !window.location.search.includes('tab=completed')) {
      const screen = screens.find(s => s.id === screenId);
      return `Historial de Mantenimientos - ${screen?.name || 'Pantalla'}`;
    }
    return "Historial de Mantenimientos";
  };

  // Manejar clic en el botón de eliminar
  const handleDeleteClick = (report: MaintenanceReport) => {
    setReportToDelete(report);
    setIsDeleteDialogOpen(true);
  };

  // Confirmar eliminación de reporte
  const handleConfirmDelete = async () => {
    if (!reportToDelete) return;
    
    try {
      const success = await deleteCompletedMaintenanceReport(reportToDelete.id);
      if (success) {
        toast.success('Registro de mantenimiento eliminado correctamente');
        loadCompletedReports(); // Recargar la lista
      } else {
        toast.error('Error al eliminar el registro de mantenimiento');
      }
    } catch (error) {
      console.error("Error al eliminar reporte:", error);
      toast.error('Error al eliminar el registro de mantenimiento');
    } finally {
      setIsDeleteDialogOpen(false);
      setReportToDelete(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">{getTitle()}</h2>
        <Badge variant="outline" className="text-sm">
          Total: {completedReports.length}
        </Badge>
      </div>
      
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input 
          placeholder="Buscar por pantalla, problema o fecha..." 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-white border-gray-200"
        />
      </div>
      
      {loading ? (
        <Card>
          <CardContent className="py-6 flex justify-center items-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2">Cargando historial de mantenimientos...</span>
          </CardContent>
        </Card>
      ) : completedReports.length === 0 ? (
        <Card>
          <CardContent className="py-6">
            <p className="text-center text-muted-foreground">
              {searchTerm ? 
                "No se encontraron mantenimientos que coincidan con la búsqueda" : 
                `No hay mantenimientos completados en el historial${screenId ? " para esta pantalla" : ""}`
              }
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pantalla</TableHead>
                  <TableHead>Problemas</TableHead>
                  <TableHead>Duración</TableHead>
                  <TableHead>Fecha de completado</TableHead>
                  <TableHead className="w-[80px]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {completedReports.map(report => {
                  const createdAt = parseISO(report.createdAt);
                  const completedAt = report.completedAt ? parseISO(report.completedAt) : new Date();
                  const duration = formatDistanceToNow(createdAt, { 
                    includeSeconds: true,
                    addSuffix: false,
                    locale: es
                  });
                  
                  return (
                    <TableRow key={report.id}>
                      <TableCell className="font-medium">
                        {report.screen?.name || `Pantalla ${report.screenId}`}
                        <div className="text-xs text-muted-foreground">
                          {report.screen?.location || 'Ubicación no especificada'}
                        </div>
                      </TableCell>
                      <TableCell>{getSelectedProblems(report.problems)}</TableCell>
                      <TableCell>{duration}</TableCell>
                      <TableCell>
                        {report.completedAt && format(parseISO(report.completedAt), 'dd/MM/yyyy HH:mm')}
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleDeleteClick(report)}
                          className="hover:bg-red-100 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Diálogo de confirmación para eliminar */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Confirma que desea eliminar este registro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El registro de mantenimiento será eliminado permanentemente del historial.
              {reportToDelete && (
                <div className="mt-2 p-3 bg-gray-50 rounded-md">
                  <div className="font-medium">{reportToDelete.screen?.name || `Pantalla ${reportToDelete.screenId}`}</div>
                  <div className="text-sm text-gray-500">
                    {reportToDelete.completedAt && format(parseISO(reportToDelete.completedAt), 'dd/MM/yyyy HH:mm')}
                  </div>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

CompletedMaintenanceList.defaultProps = {
  screenId: undefined
};

export default CompletedMaintenanceList; 